<template>
  <!--
    A phone number field: the country (its calling code) and the local
    number. The model is the stored form ("+15045551234", see utils/phone.ts).
    Typing or pasting a number with "+" picks its country by itself. The
    number input carries `id`, so a KbField label points at it; the country
    picker is "Country code" to screen readers.
  -->
  <div class="kb-phone">
    <Select
      v-model="country"
      :options="countries"
      option-label="name"
      option-value="code"
      :input-id="id ? `${id}-country` : undefined"
      aria-label="Country code"
      filter
      :filter-fields="['name', 'dial', 'code']"
      filter-placeholder="Country or code"
      size="small"
      class="kb-phone-country"
      overlay-class="kb-phone-overlay"
      :invalid="invalid"
      :disabled="disabled"
      @change="onCountryChange"
    >
      <template #value="{ value }">
        {{ value }} <span class="text-kb-dim">{{ dialCode(value) }}</span>
      </template>
      <template #option="{ option }">
        <span class="kb-phone-option">
          <span>{{ option.name }}</span>
          <span class="font-mono text-kb-dim">{{ option.dial }}</span>
        </span>
      </template>
    </Select>
    <InputText
      :id="id"
      v-model="text"
      type="tel"
      autocomplete="tel"
      inputmode="tel"
      size="small"
      fluid
      :placeholder="placeholder ?? (country === 'US' ? '(504) 555-1234' : undefined)"
      :invalid="invalid"
      :disabled="disabled"
      :aria-describedby="ariaDescribedby"
      @update:model-value="onInput"
      @blur="onBlur"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { type CountryCode, countryOfTyped, dialCode, joinPhone, normalizePhone, phoneCountries, splitPhone } from '~/utils/phone'

defineProps<{
  id?: string
  placeholder?: string
  invalid?: boolean
  disabled?: boolean
  ariaDescribedby?: string
}>()

const emit = defineEmits<{ blur: [] }>()

const model = defineModel<string | null | undefined>()

const countries = phoneCountries()

const initial = splitPhone(model.value)
const country = ref<CountryCode>(initial.country)
const text = ref(initial.national)

// Outside changes (a reset, another record) replace what's shown
watch(model, (next) => {
  if (normalizePhone(next) === joinPhone(country.value, text.value)) return
  const split = splitPhone(next, country.value)
  country.value = split.country
  text.value = split.national
})

function onInput(value: string | undefined) {
  // A number typed or pasted with its country code picks that country
  if (value?.trim().startsWith('+')) {
    const typedCountry = countryOfTyped(value)
    if (typedCountry) country.value = typedCountry
  }
  model.value = joinPhone(country.value, value)
}

function onCountryChange() {
  if (text.value.trim()) model.value = joinPhone(country.value, text.value)
  emit('blur')
}

function onBlur() {
  const split = splitPhone(model.value, country.value)
  country.value = split.country
  text.value = split.national
  emit('blur')
}
</script>
